import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import {
  Download,
  Search,
  Filter,
  ChevronDown,
  Package,
  Truck,
  MapPin,
  Check,
  X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ORDER = ["paid", "prepping", "shipped", "ready-for-pickup", "fulfilled", "cancelled"];
const STATUS_LABEL = {
  paid: "Paid",
  confirmed: "Paid",
  prepping: "Prepping",
  shipped: "Shipped",
  "ready-for-pickup": "Ready for pickup",
  fulfilled: "Fulfilled",
  cancelled: "Cancelled",
};
// Frontend-side mirror of backend transitions for button rendering.
const NEXT_STATUS = {
  paid: ["prepping", "cancelled"],
  confirmed: ["prepping", "cancelled"],
  prepping: ["shipped", "ready-for-pickup", "cancelled"],
  shipped: ["fulfilled", "cancelled"],
  "ready-for-pickup": ["fulfilled", "cancelled"],
  fulfilled: [],
  cancelled: [],
};

export default function AdminOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [fulfillFilter, setFulfillFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (fulfillFilter !== "all") params.set("fulfillment", fulfillFilter);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await axios.get(`${API}/admin/orders?${params.toString()}`);
      setOrders(data.orders || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter, fulfillFilter]);

  const totalRevenue = useMemo(
    () =>
      orders
        .filter((o) => o.status !== "cancelled")
        .reduce((s, o) => s + (o.total || 0), 0),
    [orders]
  );

  if (user && !user.is_admin) return <Navigate to="/" replace />;

  const doExport = () => {
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (fulfillFilter !== "all") params.set("fulfillment", fulfillFilter);
    // Cookie will be sent automatically since same origin
    window.location.href = `${API}/admin/orders/export.csv?${params.toString()}`;
  };

  const transition = async (order, newStatus) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/orders/${order.id}/status`,
        { status: newStatus }
      );
      setOrders((os) => os.map((o) => (o.id === data.id ? data : o)));
      if (selected?.id === data.id) setSelected(data);
      toast.success(`Order ${STATUS_LABEL[newStatus]}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div
      data-testid="admin-orders-page"
      className="min-h-screen bg-[color:var(--sr-bg)] pt-32 md:pt-40 pb-24"
    >
      <div className="sr-container">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="label-eyebrow text-[color:var(--sr-primary)] mb-3 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Admin · Orders
            </p>
            <h1 className="font-serif text-5xl md:text-7xl text-[color:var(--sr-primary)] leading-[0.95] tracking-tight">
              Every bottle,
              <br />
              <em className="italic font-light text-[color:var(--sr-accent)]">accounted for.</em>
            </h1>
          </div>
          <div className="flex items-end gap-8">
            <Stat label="Orders" value={orders.length} />
            <Stat label="Revenue" value={`$${totalRevenue.toFixed(2)}`} />
            <button
              type="button"
              onClick={doExport}
              data-testid="admin-orders-export"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors"
            >
              <Download size={14} strokeWidth={1.5} /> Export CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="border-y hairline-light py-6 mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            <Filter size={14} strokeWidth={1.5} className="text-[color:var(--sr-muted)]" />
            <span className="label-eyebrow text-[color:var(--sr-muted)]">Filter</span>
          </div>

          <Select
            testid="status-filter"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: "all", label: `All (${orders.length})` },
              ...STATUS_ORDER.map((s) => ({
                value: s,
                label: `${STATUS_LABEL[s]}${counts[s] ? ` (${counts[s]})` : ""}`,
              })),
            ]}
          />

          <Select
            testid="fulfillment-filter"
            value={fulfillFilter}
            onChange={setFulfillFilter}
            options={[
              { value: "all", label: "All fulfillments" },
              { value: "ship", label: "Ship" },
              { value: "pickup", label: "Market pickup" },
            ]}
          />

          <div className="flex-1 min-w-[220px] flex items-center gap-2 border-b hairline focus-within:border-[color:var(--sr-primary)]">
            <Search size={14} strokeWidth={1.5} className="text-[color:var(--sr-muted)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search name, email, order id"
              data-testid="orders-search"
              className="w-full bg-transparent outline-none py-2 text-[color:var(--sr-primary)] font-serif text-lg placeholder:text-[color:var(--sr-muted)]/60"
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-sm text-[color:var(--sr-muted)] py-12">Loading…</p>
        ) : orders.length === 0 ? (
          <div data-testid="admin-orders-empty" className="border hairline p-12 text-center">
            <p className="font-serif text-3xl text-[color:var(--sr-primary)]">
              No orders match these filters.
            </p>
          </div>
        ) : (
          <ul
            className="divide-y divide-[color:var(--sr-border-light)] border-y hairline-light"
            data-testid="admin-orders-list"
          >
            {orders.map((o) => (
              <li
                key={o.id}
                data-testid={`admin-order-${o.id}`}
                className="py-5 grid grid-cols-12 gap-4 items-center cursor-pointer hover:bg-[color:var(--sr-secondary)]/40 transition-colors"
                onClick={() => setSelected(o)}
              >
                <div className="col-span-12 md:col-span-4">
                  <p className="font-serif text-lg text-[color:var(--sr-primary)] leading-tight">
                    {o.name || "—"}
                  </p>
                  <p className="text-xs text-[color:var(--sr-muted)] mt-1">
                    {o.email}
                  </p>
                </div>
                <div className="col-span-6 md:col-span-2 text-xs text-[color:var(--sr-muted)]">
                  #{(o.id || "").slice(-8)}
                </div>
                <div className="col-span-6 md:col-span-2 text-xs text-[color:var(--sr-muted)]">
                  {new Date(o.created_at).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })}
                </div>
                <div className="col-span-6 md:col-span-2 flex items-center gap-2 text-xs">
                  {o.fulfillment === "pickup" ? (
                    <>
                      <MapPin size={12} strokeWidth={1.5} className="text-[color:var(--sr-accent)]" />
                      <span className="text-[color:var(--sr-primary)]">
                        {(o.market || {}).name || "Pickup"}
                      </span>
                    </>
                  ) : (
                    <>
                      <Truck size={12} strokeWidth={1.5} />
                      <span>Ship</span>
                    </>
                  )}
                </div>
                <div className="col-span-6 md:col-span-1 text-xs uppercase tracking-[0.2em]">
                  <StatusPill status={o.status || "paid"} />
                </div>
                <div className="col-span-12 md:col-span-1 md:text-right font-serif text-lg text-[color:var(--sr-primary)] tabular-nums">
                  ${(o.total || 0).toFixed(2)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Detail drawer */}
      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
              className="fixed inset-0 z-40 bg-[color:var(--sr-primary-deep)]/45 backdrop-blur-md"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[520px] bg-[color:var(--sr-bg)] flex flex-col"
              data-testid="admin-order-drawer"
            >
              <div className="flex items-center justify-between px-8 h-20 border-b hairline-light">
                <p className="label-eyebrow text-[color:var(--sr-primary)]">
                  Order #{(selected.id || "").slice(-8)}
                </p>
                <button
                  onClick={() => setSelected(null)}
                  data-testid="admin-order-drawer-close"
                  className="p-2 -mr-2 text-[color:var(--sr-text)]"
                  aria-label="Close"
                >
                  <X size={20} strokeWidth={1.25} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
                <div>
                  <p className="font-serif text-4xl text-[color:var(--sr-primary)] leading-tight">
                    {selected.name || "—"}
                  </p>
                  <p className="text-sm text-[color:var(--sr-muted)] mt-1">
                    {selected.email}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-6 border-t hairline-light pt-6">
                  <Field label="Placed" value={new Date(selected.created_at).toLocaleString()} />
                  <Field label="Total" value={`$${(selected.total || 0).toFixed(2)}`} />
                  <Field
                    label="Fulfillment"
                    value={
                      selected.fulfillment === "pickup"
                        ? `Pickup — ${(selected.market || {}).name || ""}`
                        : "Ship"
                    }
                  />
                  {selected.pickup_date && (
                    <Field label="Pickup date" value={selected.pickup_date} />
                  )}
                </div>

                <div className="border-t hairline-light pt-6">
                  <p className="label-eyebrow text-[color:var(--sr-primary)] mb-4 flex items-center gap-2">
                    <Package size={12} strokeWidth={1.5} /> Items
                  </p>
                  <ul className="space-y-3">
                    {(selected.items || []).map((it, idx) => (
                      <li
                        key={idx}
                        className="flex justify-between text-sm text-[color:var(--sr-primary)]"
                      >
                        <span>{it.name || it.lookup_key}</span>
                        <span className="text-[color:var(--sr-muted)] tabular-nums">
                          × {it.quantity}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t hairline-light pt-6">
                  <p className="label-eyebrow text-[color:var(--sr-primary)] mb-4">
                    Status — <StatusPill status={selected.status || "paid"} inline />
                  </p>
                  {NEXT_STATUS[selected.status || "paid"].length ? (
                    <div className="flex flex-wrap gap-2">
                      {NEXT_STATUS[selected.status || "paid"].map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => transition(selected, s)}
                          data-testid={`transition-${s}`}
                          className="px-4 py-2 border hairline hover:border-[color:var(--sr-primary)] label-eyebrow text-[color:var(--sr-primary)] transition-colors"
                        >
                          <Check size={12} strokeWidth={1.5} className="inline mr-1" />
                          Mark {STATUS_LABEL[s]}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[color:var(--sr-muted)]">
                      Final state — no further transitions.
                    </p>
                  )}
                </div>

                {selected.status_history?.length > 0 && (
                  <div className="border-t hairline-light pt-6">
                    <p className="label-eyebrow text-[color:var(--sr-muted)] mb-3">
                      History
                    </p>
                    <ul className="space-y-2 text-xs text-[color:var(--sr-muted)]">
                      {selected.status_history.map((h, i) => (
                        <li key={i} className="flex justify-between">
                          <span>{STATUS_LABEL[h.status] || h.status}</span>
                          <span className="tabular-nums">
                            {new Date(h.at).toLocaleString()}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="label-eyebrow text-[color:var(--sr-muted)] mb-2">{label}</p>
      <p className="font-serif text-3xl text-[color:var(--sr-primary)] tabular-nums">{value}</p>
    </div>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="appearance-none bg-transparent border hairline px-4 py-2 pr-8 label-eyebrow text-[color:var(--sr-primary)] hover:border-[color:var(--sr-primary)] outline-none transition-colors"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown
        size={12}
        strokeWidth={1.5}
        className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[color:var(--sr-muted)]"
      />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="label-eyebrow text-[color:var(--sr-muted)] mb-2">{label}</p>
      <p className="text-sm text-[color:var(--sr-primary)] break-all">{value}</p>
    </div>
  );
}

function StatusPill({ status, inline }) {
  const map = {
    paid: { bg: "rgba(184,155,94,0.12)", fg: "var(--sr-accent)" },
    prepping: { bg: "rgba(33,75,56,0.08)", fg: "var(--sr-primary)" },
    shipped: { bg: "rgba(33,75,56,0.15)", fg: "var(--sr-primary)" },
    "ready-for-pickup": { bg: "rgba(184,155,94,0.18)", fg: "var(--sr-accent)" },
    fulfilled: { bg: "rgba(33,75,56,0.85)", fg: "var(--sr-secondary)" },
    cancelled: { bg: "rgba(139,46,79,0.12)", fg: "#8B2E4F" },
  };
  const s = map[status] || map.paid;
  return (
    <span
      className={`inline-block px-2 py-1 text-[10px] tracking-[0.18em] uppercase ${inline ? "" : ""}`}
      style={{ background: s.bg, color: s.fg }}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}
